import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Spin, Typography, Tooltip } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import PurchaseOrderVoucherBody from "../../PurchaseOrderVoucher/components/PurchaseOrderVoucherBody";
import { useReactToPrint } from "react-to-print";
import { purchaseOrderVouchersApi } from "../../../../../apiservice/order";
import Swal from "sweetalert2";
import ReportDom from "../../../../../components/common/ReportDom";
import { useShareDocument } from "../../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../../components/ShareButton/ShareDocumentButton";

const { Text } = Typography;

const QuickPurchaseOrderVoucher = () => {
  const { id } = useParams();
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const reportRef = useRef(null); 

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [vouchersList, setVouchersList] = useState([]);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

  const handlePrint = useReactToPrint({
      contentRef: reportRef,
      documentTitle: "Purchase Order Voucher",
      removeAfterPrint: true,
    });
  

  useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          // 1. Fetch institution details
          const inst = await getInstitutionApi();
          setInstitution(inst.data);
  
          // 2. Fetch vouchers list
          const v = await purchaseOrderVouchersApi({});
          const list = v.data || [];
          setVouchersList(list);
  
          // 3. Auto pick the saved voucher
          const match = list.find((x) => String(x.pur_o_id) === String(id));
          if (match) {
            setSelectedVoucher(match);
          }
        } catch (err) {
          Swal.fire("Error", "Could not load voucher", "warning");
        } finally {
          setLoading(false);
        }
      };
  
      fetchData();
    }, [id]);

    useEffect(() => {
      if (shareOpen && supplierEmail) {
        setEmail(supplierEmail);
      }
    }, [shareOpen, supplierEmail, setEmail]);



  return (
    <div className="p-4">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 8,
        }}
      >
      <Button
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(-1)} 
        style={{ marginBottom: 12 }}
      >
        Back
      </Button>
      <div style={{ display: "flex", gap: 8 }}>
        <ShareDocumentButton
          disabled={!selectedVoucher}
          shareOpen={shareOpen}
          setShareOpen={setShareOpen}
          email={email}
          setEmail={setEmail}
          loading={loading}
          onEmail={() =>
            handleEmailShare({
              url: `/print/purchase-order/${selectedVoucher.pur_o_id}`,
              fileName: `PurchaseOrder_${
                voucherNo ||
                selectedVoucher.display_text ||
                "Voucher"
              }.pdf`,
              name: accName || "",
              docNo:
                voucherNo ||
                selectedVoucher.display_text ||
                "Voucher",
            })
          }
          onWhatsApp={() =>
            handleWhatsAppShare({
              url: `/print/purchase-order/${selectedVoucher.pur_o_id}`,
              fileName: `PurchaseOrder_${
                voucherNo ||
                selectedVoucher.display_text ||
                "Voucher"
              }.pdf`,
              text: `Hello ${accName || ""}, Your Purchase Order ${
                voucherNo ||
                selectedVoucher.display_text ||
                "Voucher"
              } is ready. Download here:`,
            })
          }
        />

        <Tooltip
          title={
            selectedVoucher?.approval_status === "pending"
              ? "Pending approval — Print is locked until fully approved"
              : ""
          }
        >
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={
              !selectedVoucher ||
              selectedVoucher?.approval_status === "pending"
            }
          >
            Print
          </Button>
        </Tooltip>
      </div>
      </div>
      {/* Voucher Display */}
    <Card className="shadow-md rounded-xl">

      {!selectedVoucher ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          {loading ? <Spin /> : <Text type="secondary">No voucher selected</Text>}
        </div>
      ) : (
        <PurchaseOrderVoucherBody
          pur_o_id={id}
          institution={institution}
          setAccName={setAccName}
          setVoucherNo={setVoucherNo}
          setSupplierEmail={setSupplierEmail}
        />
      )}
      </Card>  

      {/* Hidden print area */}
      {selectedVoucher && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={reportRef}
            title="Purchase Order Voucher"
            renderTable={() => (
              <PurchaseOrderVoucherBody
                pur_o_id={selectedVoucher.pur_o_id}
                institution={institution}
                printMode
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default QuickPurchaseOrderVoucher;
